import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Users, Sparkles, Shield, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const WHATSAPP_NUMBER = '5594992013575';
const WHATSAPP_MESSAGE = encodeURIComponent(`Olá! Gostaria de reservar um quarto no Hotel Del Rio.
Datas desejadas:
Número de hóspedes:
Tipo de quarto:`);

export const Home = () => {
  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`, '_blank');
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      <section 
        className="relative h-screen flex items-center justify-center"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_delrio-booking/artifacts/626e86rc_WhatsApp%20Image%202026-02-01%20at%2022.44.53%20%281%29.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to bottom, rgba(27, 77, 62, 0.4), rgba(27, 77, 62, 0.8))'
          }}
        />
        
        <div className="relative z-10 text-center text-white max-w-4xl px-4">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-display font-bold mb-6 tracking-tight" data-testid="hero-title">
            Bem-vindo ao Hotel Del Rio
          </h1>
          <p className="text-lg sm:text-xl mb-8 text-white/90 max-w-2xl mx-auto">
            Uma experiência única de hospitalidade no coração de Conceição do Araguaia
          </p>
          <Button 
            size="lg" 
            className="bg-secondary hover:bg-secondary/90 text-primary rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
            onClick={handleWhatsAppClick}
            data-testid="cta-reserve-now"
          >
            <MessageCircle className="h-5 w-5 mr-2" />
            Reservar agora pelo WhatsApp
          </Button>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-display font-bold text-primary mb-4">
              Por que escolher o Hotel Del Rio?
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Conforto, elegância e hospitalidade amazônica em cada detalhe
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-8 bg-background rounded-xl border border-border/50 hover:border-secondary/50 transition-colors" data-testid="feature-comfort">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Sparkles className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-display font-semibold mb-3">Conforto Premium</h3>
              <p className="text-text-secondary">
                Quartos espaçosos com amenidades de primeira classe para sua comodidade
              </p>
            </div>

            <div className="p-8 bg-background rounded-xl border border-border/50 hover:border-secondary/50 transition-colors" data-testid="feature-location">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Calendar className="h-4 w-4 text-primary" />
              </div>
              <h3 className="text-xl font-display font-semibold mb-3">Reserva Fácil</h3>
              <p className="text-text-secondary">
                Atendimento rápido e personalizado via WhatsApp
              </p>
            </div>

            <div className="p-8 bg-background rounded-xl border border-border/50 hover:border-secondary/50 transition-colors" data-testid="feature-service">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-display font-semibold mb-3">Atendimento Dedicado</h3>
              <p className="text-text-secondary">
                Equipe pronta para atender e garantir sua melhor experiência
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_delrio-booking/artifacts/xhkub7iy_image.png"
                alt="Quarto de luxo"
                className="rounded-xl shadow-lg w-full h-96 object-cover"
              />
            </div>
            <div>
              <h2 className="text-4xl font-display font-bold text-primary mb-6">
                Quartos que Inspiram
              </h2>
              <p className="text-text-secondary mb-6 leading-relaxed">
                Nossos quartos combinam design elegante com o calor da hospitalidade amazônica. 
                Cada espaço foi pensado para proporcionar momentos de descanso e renovação.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <span className="text-text-primary">Ar-condicionado e Wi-Fi de alta velocidade</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <span className="text-text-primary">Quartos com vista para a natureza</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <span className="text-text-primary">Café da manhã regional incluído</span>
                </li>
              </ul>
              <Link to="/rooms">
                <Button className="bg-primary hover:bg-primary/90 rounded-full px-8" data-testid="view-rooms-button">
                  Ver Todos os Quartos
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-display font-bold text-primary mb-4">
            Pronto para sua próxima aventura?
          </h2>
          <p className="text-text-secondary mb-8 max-w-2xl mx-auto">
            Entre em contato agora pelo WhatsApp. Cancelamento gratuito até 7 dias antes.
          </p>
          <Button 
            size="lg" 
            className="bg-accent hover:bg-accent/90 text-white rounded-full px-10 py-6 text-lg shadow-lg"
            onClick={handleWhatsAppClick}
            data-testid="cta-book-now"
          >
            <MessageCircle className="h-5 w-5 mr-2" />
            Reservar agora pelo WhatsApp
          </Button>
        </div>
      </section>
    </div>
  );
};
